import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import CustomModal from "../CustomModal";
import EnrollStudentInProgramModal from "./EnrollStudentInProgramModal";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

function InstituteEnrollProgramCard({ programs = [], onViewMore, expandedProgram, hideImportEnroll }) {
  const [showNavigation, setShowNavigation] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [currentProgramId, setCurrentProgramId] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [isTextTruncated, setIsTextTruncated] = useState({});
  const descriptionRefs = useRef({});

  const safePrograms = Array.isArray(programs) ? programs : [];

  // API Configuration
  const API_BASE_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const AWS = process.env.REACT_APP_AWS_HOST;
  const USER_ID = sessionStorage.getItem('id');
  const INSTITUTE_ID = sessionStorage.getItem('instituteId');
  const INSTITUTE_TYPE = sessionStorage.getItem('instituteType');

  // Function to determine if navigation should be shown based on screen width
  const checkNavigationNeeded = () => {
    const screenWidth = window.innerWidth;
    let slidesPerView = 3; // default

    if (screenWidth < 768) {
      slidesPerView = 1;
    } else if (screenWidth < 1150) {
      slidesPerView = 2;
    } else {
      slidesPerView = 3;
    }

    setShowNavigation(safePrograms.length > slidesPerView);
  };

  useEffect(() => {
    if (safePrograms.length > 0) {
      checkNavigationNeeded();
      window.addEventListener("resize", checkNavigationNeeded);

      return () => {
        window.removeEventListener("resize", checkNavigationNeeded);
      };
    }
  }, [safePrograms.length]);

  // Check if text is truncated for each program description
  useEffect(() => {
    const checkTruncation = () => {
      safePrograms.forEach((program) => {
        const element = descriptionRefs.current[program.id];
        if (element) {
          const clone = element.cloneNode(true);
          clone.style.position = 'absolute';
          clone.style.visibility = 'hidden';
          clone.style.height = 'auto';
          clone.style.maxHeight = 'none';
          clone.style.webkitLineClamp = 'none';
          clone.style.display = 'block';
          element.parentNode.appendChild(clone);

          const isOverflowing = clone.offsetHeight > element.offsetHeight;
          setIsTextTruncated((prev) => ({
            ...prev,
            [program.id]: isOverflowing,
          }));

          element.parentNode.removeChild(clone);
        }
      });
    };

    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [safePrograms]);

  // Handle mouse enter for tooltip
  const handleMouseEnter = (e, programId, text) => {
    if (!text || text === "Description is not available") return;
    if (isTextTruncated[programId]) {
      const rect = e.currentTarget.getBoundingClientRect();
      const tooltipMaxWidth = 360;
      let x = rect.left;
      let y = rect.bottom + 6;
      let placement = "bottom";

      if (x + tooltipMaxWidth > window.innerWidth - 16) {
        x = Math.max(16, window.innerWidth - tooltipMaxWidth - 16);
      }
      if (x < 16) {
        x = 16;
      }

      if (y + 160 > window.innerHeight && rect.top > 160) {
        y = rect.top - 6;
        placement = "top";
      }

      setActiveTooltip({
        text,
        x,
        y,
        placement,
      });
    }
  };

  // Handle mouse leave for tooltip
  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  // Calculate progress percentage for credit progress bar
  const calculateProgressPercentage = (creditPurchased, creditBalance) => {
    const purchased = parseFloat(creditPurchased) || 0;
    const balance = parseFloat(creditBalance) || 0;
    const used = purchased - balance;

    if (purchased === 0) return 0;
    return Math.min((used / purchased) * 100, 100);
  };

  // Function to get program image or fallback
  const getProgramImage = (program) => {
    if (program.image) {
      return program.image;
    }
    if (program.program_image) {
      return program.program_image;
    }
    if (program.thumbnail) {
      return program.thumbnail;
    }

    return null;
  };

  // Get clean description text
  const getCleanDescription = (program) => {
    return program.description?.replace(/<[^>]*>/g, '') || 'Description is not available';
  };

  // Import Student Modal Functions
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setUploadError("");
    setUploadSuccess("");

    if (!file) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    const isValidType = validTypes.includes(file.type) || /\.(csv|xlsx|xls)$/i.test(file.name);

    if (isValidType) {
      setSelectedFile(file);
    } else {
      setUploadError('Please select a valid CSV or Excel file.');
      event.target.value = '';
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadError("");
    setUploadSuccess("");

    const fileInput = document.getElementById('importFileInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleImportStudent = (programId) => {
    setCurrentProgramId(programId);
    setShowImportModal(true);
  };

  const handleEnrollStudent = (programId) => {
    setCurrentProgramId(programId);
    setShowEnrollModal(true);
  };

  const handleCloseEnrollModal = () => {
    setShowEnrollModal(false);
    setCurrentProgramId(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file first.');
      return;
    }

    if (!currentProgramId) {
      setUploadError('Program ID is missing. Cannot upload students.');
      return;
    }

    setIsUploading(true);
    setUploadError("");
    setUploadSuccess("");

    try {
      const formData = new FormData();
      formData.append('user_id', USER_ID);
      formData.append('student_file', selectedFile);
      formData.append('program_id', currentProgramId);
      formData.append('institute_id', INSTITUTE_ID);

      const response = await fetch(`${API_BASE_URL}/api/upload-students`, {
        method: 'POST',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json',
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        const successMessage = result.message || 'Students uploaded successfully!';
        setUploadSuccess(successMessage);

        setTimeout(() => {
          setShowImportModal(false);
          setSelectedFile(null);
          setUploadSuccess("");
          setCurrentProgramId(null);

          const fileInput = document.getElementById('importFileInput');
          if (fileInput) {
            fileInput.value = '';
          }
        }, 2000);
      } else {
        let errorMessage;
        if (result.errors) {
          const errorMessages = Object.values(result.errors).flat();
          errorMessage = errorMessages.join(', ');
        } else if (result.message) {
          errorMessage = result.message;
        } else {
          errorMessage = 'Upload failed. Please try again.';
        }
        setUploadError(errorMessage);
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setShowImportModal(false);
    setSelectedFile(null);
    setUploadError("");
    setUploadSuccess("");
    setCurrentProgramId(null);

    const fileInput = document.getElementById('importFileInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <>
      <div className="card-body padnone">
        <div style={{ position: "relative" }}>
          <Swiper
            spaceBetween={20}
            slidesPerView={3}
            style={{ paddingBottom: "1rem" }}
            pagination={{ clickable: true }}
            navigation={
              showNavigation
                ? {
                    nextEl: ".swiper-button-next-custom-programs",
                    prevEl: ".swiper-button-prev-custom-programs",
                  }
                : false
            }
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            modules={[Pagination, Navigation, Autoplay]}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1150: { slidesPerView: 3 },
            }}
          >
            {safePrograms.map((program, idx) => {
              const progressPercentage = calculateProgressPercentage(
                program.credit_purchased,
                program.credit_balance
              );

              const programImage = getProgramImage(program);
              const cleanDescription = getCleanDescription(program);

              return (
                <SwiperSlide key={program.id || idx}>
                  <div
                    className="gallery-box card fillcolor"
                    style={{ padding: 0, cursor: "pointer" }}
                    onClick={() => onViewMore(program.id)}
                  >
                    <div
                      className="card mb-0 yellowborder"
                      style={{ minHeight: "400px" }}
                    >
                      <div
                        className="card-img-top"
                        style={{
                          height: "200px",
                          overflow: "hidden",
                          position: "relative",
                          borderTopLeftRadius: "8px",
                          borderTopRightRadius: "8px",
                        }}
                      >
                        {programImage ? (
                          <img
                            src={`${AWS}${programImage}`}
                            alt={program.program_title}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.3s ease",
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}

                        <div
                          style={{
                            position: programImage ? "absolute" : "static",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: `linear-gradient(135deg, 
                              ${['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe'][idx % 5]} 0%, 
                              ${['#764ba2', '#667eea', '#f5576c', '#f093fb', '#00f2fe'][idx % 5]} 100%)`,
                            display: programImage ? "none" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: "24px",
                            fontWeight: "bold",
                            textAlign: "center",
                            padding: "20px",
                          }}
                        >
                          <div>
                            <i className="ri-book-open-line" style={{ fontSize: "48px", marginBottom: "10px", display: "block" }}></i>
                            {program.program_title}
                          </div>
                        </div>

                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            background: "linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.3) 100%)",
                            pointerEvents: "none",
                          }}
                        ></div>
                      </div>

                      <div className="card-body">
                        <div className="d-flex position-relative">
                          <div style={{ width: "100%" }}>
                            <h5 className="mt-0" style={{ height: "35px", fontSize: "16px", fontWeight: "600" }}>
                              {program.program_title}
                            </h5>
                            <div style={{ position: "relative" }}>
                              <p
                                ref={(el) => (descriptionRefs.current[program.id] = el)}
                                className="text-truncate-two-lines mb-2 primary"
                                style={{
                                  height: "38px",
                                  fontSize: "14px",
                                  cursor: isTextTruncated[program.id] ? "help" : "pointer",
                                  position: "relative",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "normal",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                                onMouseEnter={(e) => handleMouseEnter(e, program.id, cleanDescription)}
                                onMouseLeave={handleMouseLeave}
                              >
                                {cleanDescription}
                              </p>
                            </div>

                            <p className="mb-3 primary" style={{ fontSize: "13px" }}>
                              Total Hours : {program.duration}
                              {program.total_courses}
                            </p>

                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="fw-semibold primary">License Progress</small>
                                <small className="text-primary">
                                  {(parseFloat(program.credit_purchased) || 0) - (parseFloat(program.credit_balance) || 0)}/
                                  {parseFloat(program.credit_purchased) || 0}
                                </small>
                              </div>
                              <div
                                className="progress"
                                style={{
                                  height: "8px",
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${progressPercentage}%`,
                                    backgroundColor: progressPercentage >= 80 ? "#dc3545" :
                                                   progressPercentage >= 60 ? "#ffc107" : "#28a745",
                                    transition: "width 0.3s ease",
                                  }}
                                  aria-valuenow={progressPercentage}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-primary">
                                  Balance: {parseFloat(program.credit_balance) || 0}
                                </small>
                                <small className="text-primary">
                                  {progressPercentage.toFixed(1)}% Used
                                </small>
                              </div>
                            </div>

                            <div className="d-flex gap-2 mb-2">
                              {!hideImportEnroll && INSTITUTE_TYPE?.toLowerCase() === 'school' ? (
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImportStudent(program.id);
                                  }}
                                  style={{
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                    width: "50%",
                                    backgroundColor: "#36348e",
                                    color: "white",
                                  }}
                                >
                                  <i className="ri-upload-line me-1"></i>
                                  Import Student
                                </button>
                              ) : !hideImportEnroll ? (
                                <button
                                  className="btn btn-sm"
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEnrollStudent(program.id);
                                  }}
                                  style={{
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                    width: "50%",
                                    backgroundColor: "#36348e",
                                    color: "white",
                                  }}
                                >
                                  <i className="ri-user-add-line me-1"></i>
                                  Enroll Student
                                </button>
                              ) : null}

                              <button
                                className={`btn btn-sm ${
                                  expandedProgram === program.id
                                    ? "btn-danger"
                                    : "btn-warning"
                                }`}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewMore(program.id);
                                }}
                                style={{ width: hideImportEnroll ? "100%" : "50%" }}
                              >
                                {expandedProgram === program.id ? (
                                  <>
                                    Hide Details <i className="ri-arrow-up-line"></i>
                                  </>
                                ) : (
                                  <>
                                    View Details{" "}
                                    <i className="ri-arrow-down-line"></i>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>

          {showNavigation && (
            <>
              <div
                className="swiper-button-prev-custom-programs"
                style={{
                  position: "absolute",
                  top: "40%",
                  left: "-20px",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: "1px solid #e9ecef",
                }}
              >
                <i
                  className="ri-arrow-left-s-line"
                  style={{ fontSize: "18px", color: "#6c757d" }}
                ></i>
              </div>

              <div
                className="swiper-button-next-custom-programs"
                style={{
                  position: "absolute",
                  top: "40%",
                  right: "-20px",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  width: "40px",
                  height: "40px",
                  backgroundColor: "#fff",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  border: "1px solid #e9ecef",
                }}
              >
                <i
                  className="ri-arrow-right-s-line"
                  style={{ fontSize: "18px", color: "#6c757d" }}
                ></i>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Import Student Modal for Schools */}
      <CustomModal
        show={showImportModal}
        onClose={handleCancel}
        title="Import Student"
        width={700}
      >
        <div
          className="import-modal-content"
          style={{
            marginTop: '20px',
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          }}
        >
          {uploadError && (
            <div
              style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{uploadError}</span>
            </div>
          )}

          {uploadSuccess && (
            <div
              style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i className="bi bi-check-circle-fill"></i>
              <span>{uploadSuccess}</span>
            </div>
          )}

          <div className="file-upload-section" style={{ marginBottom: '30px' }}>
            <div className="d-flex flex-column mb-2 flex-sm-row aligns-item-center justify-content-between">
              <label
                htmlFor="importFileInput"
                style={{
                  display: 'block',
                  marginBottom: '15px',
                  fontWeight: '600',
                  color: '#2c3e50',
                  fontSize: '18px',
                  letterSpacing: '0.5px',
                }}
              >
                Upload CSV or Excel File:
              </label>
              <div className="text-right">
                <button
                  className="btn btn-sm"
                  type="button"
                  style={{ backgroundColor: "#36348e", color: "white" }}
                >
                  <a
                    href={`${AWS}uploads/sample/sample-csv-format.xlsx`}
                    className="text-white"
                    download="sample-csv-format.xlsx"
                  >
                    <i className="ri-download-2-line me-1"></i>
                    Sample csv format
                  </a>
                </button>
              </div>
            </div>
            <div
              style={{
                border: '2px dashed #36348E',
                borderRadius: '12px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                transition: 'all 0.3s ease',
              }}
            >
              <input
                type="file"
                id="importFileInput"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              />
            </div>
            {selectedFile && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                  }}
                >
                  <i
                    className="bi bi-file-earmark-text"
                    style={{ color: '#36348E' }}
                  ></i>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#36348E',
                      fontWeight: '500',
                    }}
                  >
                    {selectedFile.name}
                  </p>
                </div>
                <button
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    fontSize: '16px',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isUploading ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading) {
                      e.target.style.backgroundColor = '#f8d7da';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                  title="Remove file"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}
          </div>

          <hr
            style={{
              border: 'none',
              borderTop: '2px solid #f0f0f0',
              margin: '25px 0',
            }}
          />

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '15px',
            }}
          >
            <button
              onClick={handleCancel}
              disabled={isUploading}
              style={{
                padding: '12px 24px',
                border: '2px solid #36348E',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#36348E',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                opacity: isUploading ? 0.7 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={isUploading || !selectedFile}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: isUploading || !selectedFile ? '#ccc' : '#36348E',
                color: 'white',
                cursor: isUploading || !selectedFile ? 'not-allowed' : 'pointer',
                fontSize: '15px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 6px rgba(54, 52, 142, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {isUploading ? (
                <>
                  <i
                    className="bi bi-arrow-clockwise"
                    style={{
                      animation: 'spin 1s linear infinite',
                    }}
                  ></i>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload"></i>
                  Upload
                </>
              )}
            </button>
          </div>
        </div>

        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(-5px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}
        </style>
      </CustomModal>

      {/* Enroll Student Modal for Colleges */}
      <EnrollStudentInProgramModal
        show={showEnrollModal}
        onClose={handleCloseEnrollModal}
        programId={currentProgramId}
      />

      {activeTooltip &&
        createPortal(
          <div
            className="tooltip-custom-portal"
            style={{
              position: "fixed",
              left: `${activeTooltip.x}px`,
              top: `${activeTooltip.y}px`,
              transform: activeTooltip.placement === "top" ? "translateY(-100%)" : "none",
              backgroundColor: "#ffffff",
              color: "#334155",
              padding: "10px 14px",
              borderRadius: "8px",
              maxWidth: "360px",
              fontSize: "13px",
              fontWeight: 500,
              lineHeight: "1.45",
              wordWrap: "break-word",
              zIndex: 999999,
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              border: "1px solid #cbd5e1",
              pointerEvents: "none",
              animation: "fadeInTooltip 0.15s ease-in-out",
            }}
          >
            {activeTooltip.text}
          </div>,
          document.body
        )}
    </>
  );
}

export default InstituteEnrollProgramCard;